import * as ChapterType from "utils/ChapterType";
import * as HotspotTargetType from "utils/HotspotTargetType";

import once from "lodash/once";
import get from "lodash/get";
import isUndefined from "lodash/isUndefined";
import includes from "lodash/includes";
import isFunction from "lodash/isFunction";
import mapKeys from "lodash/mapKeys";
import pick from "lodash/pick";

import getCurrentVideoId from "selectors/video/getCurrentVideoId";
import getPlaybackState from "selectors/chapter/getPlaybackState";
import InterviewUtils from "utils/InterviewUtils";
import reduxStore from "utils/storeFactory";
import { getChapterEntity } from "selectors/chapter/makeGetChapterEntity";
import { makeGetSlideshowChapterFromSlideId } from "selectors/chapter/slideshow/makeGetSlideshowChapterFromSlideId";
import { slugify, titleize } from "underscore.string";
import invariant from "invariant";
import Tracker from "./Tracker";
import { addRefEvents, flushRefEvents } from "./bindings/refEvents";
import { getMediaType } from "../ChapterTypeHelpers";
import getMediaId from "./utils/getMediaId";
import AnalyticsTrackingTypes from "./types/AnalyticsTrackingTypes";
import addHandlerWrappers from "./bindings/addHandlerWrappers";
import * as UIInteractedTypeType from "./types/UIInteractedTypeType";
import * as UIInteractedItemType from "./types/UIInteractedItemType";
import * as UIInteractedCategoryType from "./types/UIInteractedCategoryType";
import * as MediaTypeType from "./types/MediaTypeType";
import * as LinkConvertedClickTargetType from "./types/LinkConvertedClickTargetType";
import * as InteractionType from "./types/InteractionType";
import { notifyExternalMediaPlayback } from "../externalMessaging";

export {
  AnalyticsTrackingTypes,
  LinkConvertedClickTargetType,
  MediaTypeType,
  UIInteractedCategoryType,
  UIInteractedItemType,
  UIInteractedTypeType,
};

function isLastEventSlideshowArrowClick() {
  const lastEvent = Tracker.lastEvent;
  return lastEvent.item === UIInteractedItemType.SLIDESHOW_ARROW_CLICK;
}

function isLastEventSlideshowSwipe() {
  const lastEvent = Tracker.lastEvent;
  return lastEvent.item === UIInteractedItemType.SLIDESHOW_SWIPE;
}

function slideViewedBecauseOfRecentTimelineClick() {
  return Tracker.slideViewedBecauseOfRecentTimelineClick;
}

export function blockSlideViewedComplete() {
  return (
    isLastEventSlideshowArrowClick() ||
    isLastEventSlideshowSwipe() ||
    slideViewedBecauseOfRecentTimelineClick()
  );
}

function primeAnalyticsEvent({
  chapterEntity,
  chapterType,
  durationMs,
  chapterId,
  percentagePlayed,
  playbackState,
  timeMs,
}) {
  const mediaId = getMediaId({
    chapterId,
    playbackState,
  });

  const videoId = getCurrentVideoId({
    chapterId,
    playbackState,
  });

  let mediaData = {
    chapterId,
    chapterTitle: chapterEntity.title,
    chapterType: titleize(chapterType),
    durationMs,
    mediaId,
    mediaType: getMediaType(chapterType),
    percentagePlayed,
    secondsPlayed: timeMs / 1000,
  };

  if (videoId) {
    mediaData = {
      ...mediaData,
      videoId,
    };
  }

  return mediaData;
}

window.chapterQuartileReportingHistory =
  window.chapterQuartileReportingHistory ?? new Map();
const chapterQuartileReportingHistory = window.chapterQuartileReportingHistory;

function reportQuartiles({ chapterId, playbackState, reduxState, timeMs }) {
  invariant(typeof chapterId !== "undefined", "chapterId is required");
  invariant(typeof reduxState !== "undefined", "reduxState is required");

  const chapterEntity = getChapterEntity(reduxState, { chapterId });
  playbackState = playbackState ?? getPlaybackState(reduxState, { chapterId });
  timeMs = timeMs ?? playbackState.time;

  let durationMs;
  if (chapterEntity.type === ChapterType.INTERVIEW) {
    durationMs = playbackState.currentQuestion
      ? playbackState.currentQuestion.duration
      : playbackState.duration;
  } else if (chapterEntity.type === ChapterType.PATHFINDER) {
    // Shorten by 10 ms so that the loop doesn't send continually
    durationMs = chapterEntity.introductionVideo
      ? chapterEntity.introductionVideo.duration - 10
      : null;
  } else {
    durationMs = chapterEntity.duration;
  }

  // Calculate the percent played and report true round number
  const percentagePlayed = Math.round((100 * timeMs) / durationMs);

  [25, 50, 75, 100].forEach((quartile) => {
    const mediaId = getMediaId({
      chapterId,
      playbackState,
    });

    if (!chapterQuartileReportingHistory.has(mediaId)) {
      chapterQuartileReportingHistory.set(mediaId, new Set());
    }

    if (
      percentagePlayed >= quartile &&
      !chapterQuartileReportingHistory.get(mediaId).has(quartile)
    ) {
      chapterQuartileReportingHistory.get(mediaId).add(quartile);

      Tracker.sendQuartile({
        ...primeAnalyticsEvent({
          chapterEntity,
          chapterType: chapterEntity.type,
          durationMs,
          chapterId,
          percentagePlayed,
          playbackState,
          timeMs,
        }),
        quartile,
      });
    }
  });
}

// Passthrough `Tracker.setViewer`.
const setViewer = Tracker.setViewer;
export { setViewer };

export const ARRIVED_VIA = {
  ROOT_LINK: "RootLink",
  DEEP_LINK: "DeepLink",
};

// Segment.io API Key
const SEGMENT_API_KEY = "A4canvOUP7bIBKhVlmcbvOjtSWStX3eX"; //eslint-disable-line

// Store a `Map` to hold all the event listeners.
const eventListeners = new Map();

// Store a list of videoProgress events that have been fired from Videos.
const videoPlaybackProgressPercentage = new Map();
const videoPlaybackProgressTime = new Map();

export const AnalyticsActions = {
  // [`Story Viewed`](https://github.com/MeteorMag/CMS/blob/e05a6eb57f99d498ea628bb33bb0ac9adb997b6a/server/myproject/myproject/versetest/static/versetest/spec/analytic_spec/README.md#story-viewed)
  storyViewed({ arrivedVia, route, storyAuthor, storyId, storyTitle }) {
    // Set default ID if it wasn't set.
    if (!storyId) {
      storyId = slugify(storyTitle);
    }

    // Persist story data to `Tracker`.
    Tracker.setStoryData({
      author: storyAuthor,
      id: storyId,
      title: storyTitle,
    });

    // Track.
    Tracker.send(
      {
        arrivedVia,
        event: AnalyticsTrackingTypes.storyViewed,
        route,
      },
      {
        determineInteractionType: true,
      },
    );
  },

  singleVideoLoaded() {
    Tracker.send({ event: AnalyticsTrackingTypes.singleVideoLoaded });
  },

  singleVideoViewed() {
    Tracker.send({ event: AnalyticsTrackingTypes.singleVideoViewed });
  },

  slideViewed(props) {
    const reduxState = reduxStore.getState();
    const chapterEntity = makeGetSlideshowChapterFromSlideId()(reduxState, {
      slideId: props.slideId,
    });
    const slideEntity = reduxState.story.entities.slide[props.slideId];

    Tracker.send(
      {
        event: AnalyticsTrackingTypes.slideViewed,
        interactionType: InteractionType.PASSIVE,
        chapterId: chapterEntity.id,
        chapterTitle: chapterEntity.title,
        slideId: props.slideId,
        slideType: slideEntity.video ? "video" : "image",
      },
      {
        determineInteractionType: true,
      },
    );
  },

  slideViewedComplete({ slideId }) {
    const reduxState = reduxStore.getState();
    const chapterEntity = makeGetSlideshowChapterFromSlideId()(reduxState, {
      slideId,
    });
    const slideEntity = reduxState.story.entities.slide[slideId];
    const slideType = slideEntity.video ? "video" : "image";

    Tracker.send({
      event: AnalyticsTrackingTypes.slideViewedComplete,
      interactionType: InteractionType.PASSIVE,
      chapterId: chapterEntity.id,
      chapterTitle: chapterEntity.title,
      slideId,
      slideType,
    });
  },

  slideshowArrowClick({ direction }) {
    Tracker.sendUIInteraction({
      item: UIInteractedItemType.SLIDESHOW_ARROW_CLICK,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.NAVIGATION,
      direction,
    });
  },

  // https://github.com/MeteorMag/CMS/blob/e05a6eb57f99d498ea628bb33bb0ac9adb997b6a/server/myproject/myproject/versetest/static/versetest/spec/analytic_spec/README.md#chapter-viewed
  chapterViewed(props, state) {
    const currentTime = get(state, "playbackState.time", 0);
    let event;
    if (currentTime > 0) {
      event = AnalyticsTrackingTypes.chapterResumed;
    } else {
      event = AnalyticsTrackingTypes.chapterViewed;
    }

    Tracker.send(
      {
        event,
        chapterId: props.chapterId,
        chapterTitle: props.chapterTitle,
        chapterType: props.chapterType,
        levelDepth: props.isSubchapter ? 2 : 1,
      },
      {
        determineInteractionType: true,
      },
    );
    AnalyticsActions.mediaProgress(props, state, 0);
  },

  hotspotOffered(props) {
    let targetId;
    let targetUrl;
    let originationChapterId;

    if (props.targetChapterId) {
      targetId = props.targetChapterId;
      originationChapterId = props.originationChapterId;
    } else {
      originationChapterId = props.chapterId;

      if (props.target.type === HotspotTargetType.CHAPTER) {
        targetId = props.target.chapterData.id;
      } else if (props.target.type === HotspotTargetType.LINK) {
        targetUrl = props.target.target;
      }
    }

    Tracker.sendLinkOffered({
      chapterId: originationChapterId,
      cuepointId:
        props.id || `${originationChapterId} -> ${targetId || targetUrl}`,
      targetId,
      targetUrl,
      type: props.hotspotType,
    });
  },

  cuepointClick(props, state, cuepoint) {
    if (cuepoint.target.type === HotspotTargetType.CHAPTER) {
      Tracker.sendCuepointLinkConverted({
        chapterId: props.chapterId,
        clickTarget: LinkConvertedClickTargetType.CUEPOINT,
        cuepointId:
          props.id || `${props.chapterId} -> ${cuepoint.target.chapterId}`,
        targetId: cuepoint.target.chapterId,
        time: props.time,
      });
    } else if (cuepoint.target.type === HotspotTargetType.LINK) {
      Tracker.sendCuepointLinkConverted({
        chapterId: props.chapterId,
        clickTarget: LinkConvertedClickTargetType.CUEPOINT,
        cuepointId:
          props.id || `${props.chapterId} -> ${cuepoint.target.target}`,
        targetUrl: cuepoint.target.target,
        time: props.time,
      });
    } else if (cuepoint.target.type === HotspotTargetType.EXTERNAL_CALLBACK) {
      Tracker.sendCuepointLinkConverted({
        callbackIdentifier: cuepoint.target.identifier,
        chapterId: props.chapterId,
        clickTarget: LinkConvertedClickTargetType.CUEPOINT,
        cuepointId:
          props.id || `${props.chapterId} -> ${cuepoint.target.identifier}`,
        targetUrl: cuepoint.target.target,
        time: props.time,
      });
    } else {
      throw new Error("Cuepoint clicked and no analytics defined");
    }
  },

  // https://github.com/MeteorMag/CMS/blob/e05a6eb57f99d498ea628bb33bb0ac9adb997b6a/server/myproject/myproject/versetest/static/versetest/spec/analytic_spec/README.md#chapter-viewed-complete
  chapterViewedComplete({ chapterId }) {
    const reduxState = reduxStore.getState();
    const chapterEntity = getChapterEntity(reduxState, { chapterId });
    const playbackState = getPlaybackState(reduxState, { chapterId });
    const event = AnalyticsTrackingTypes.chapterViewedComplete;

    // Send along a 100% progress event too.
    AnalyticsActions.mediaProgress(
      { chapterId, playbackState },
      null,
      chapterEntity.duration,
    );

    // Send slideViewedComplete if this is a slideshow.
    if (chapterEntity.type === "slideshow") {
      AnalyticsActions.slideViewedComplete({
        slideId: chapterEntity.slides[chapterEntity.slides.length - 1],
      });
    }

    Tracker.send({
      event,
      chapterId,
      chapterTitle: chapterEntity.title,
      chapterType: chapterEntity.type,
      levelDepth: chapterEntity.parent ? 2 : 1,
      interactionType: InteractionType.PASSIVE,
    });
  },

  bitrateReported({ oldQuality, newQuality, videoId, chapterId }) {
    const qualityPropsToPick = ["bitrate", "width", "height"];
    let eventData = {};
    if (oldQuality) {
      eventData = {
        ...eventData,
        ...mapKeys(
          pick(oldQuality, qualityPropsToPick),
          (value, key) => `old${key[0].toUpperCase()}${key.substring(1)}`,
        ),
      };
    }
    if (newQuality) {
      eventData = {
        ...eventData,
        ...mapKeys(
          pick(newQuality, qualityPropsToPick),
          (value, key) => `new${key[0].toUpperCase()}${key.substring(1)}`,
        ),
      };
    }
    eventData = {
      ...eventData,
      videoId,
      chapterId,
      mediaType: "video",
      event: AnalyticsTrackingTypes.bitrateReported,
    };
    Tracker.send(eventData);
  },

  returnToParentChapter() {
    Tracker.sendUIInteraction({
      item: UIInteractedItemType.RETURN_TO_PARENT_CHAPTER,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.NAVIGATION,
    });
  },

  browserNavigation(isPassive) {
    Tracker.sendUIInteraction(
      {
        item: UIInteractedItemType.BROWSER_NAVIGATION,
        type: UIInteractedTypeType.CLICK,
        uiCategory: UIInteractedCategoryType.NAVIGATION,
      },
      { isPassive },
    );
  },

  chapterButton() {
    Tracker.sendUIInteraction({
      item: UIInteractedItemType.CHAPTER_BUTTON,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.NAVIGATION,
    });
  },

  chapterMenuViewed() {
    Tracker.sendUIInteraction({
      item: UIInteractedItemType.CHAPTER_MENU_VIEWED,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.PRESENTATION,
    });
  },

  chapterMenuClosed(props, state, options) {
    Tracker.sendUIInteraction(
      {
        item: UIInteractedItemType.CHAPTER_MENU_CLOSED,
        type: UIInteractedTypeType.CLICK,
        uiCategory: UIInteractedCategoryType.PRESENTATION,
      },
      options,
    );
  },

  childChapterViewed() {
    Tracker.sendUIInteraction({
      item: UIInteractedItemType.CHILD_CHAPTER_VIEWED,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.NAVIGATION,
    });
  },

  shareLinkClicked(type) {
    Tracker.sendUIInteraction({
      item: `Share ${type} clicked`,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.PRESENTATION,
    });
  },

  storyTitleClick() {
    Tracker.sendPassiveUIInteraction({
      item: UIInteractedItemType.STORY_TITLE_CLICK,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.MEDIA_PLAYBACK,
    });
  },

  slideshowSwipe() {
    Tracker.sendUIInteraction({
      item: UIInteractedItemType.SLIDESHOW_SWIPE,
      type: UIInteractedTypeType.TOUCH,
      uiCategory: UIInteractedCategoryType.NAVIGATION,
    });
  },

  storyListViewed() {
    Tracker.sendUIInteraction({
      item: UIInteractedItemType.STORY_LIST_VIEWED,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.NAVIGATION,
    });
  },

  timelineShowClick() {
    Tracker.sendUIInteraction({
      item: UIInteractedItemType.TIMELINE_SHOW_CLICK,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.NAVIGATION,
    });
  },

  timelineCaptionClick(props) {
    const item = props.isShowingSlideshowCaptions
      ? UIInteractedItemType.TIMELINE_CAPTION_HIDE_CLICK
      : UIInteractedItemType.TIMELINE_CAPTION_SHOW_CLICK;

    Tracker.sendUIInteraction({
      item,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.NAVIGATION,
    });
  },

  timelineHideClick() {
    Tracker.sendUIInteraction({
      item: UIInteractedItemType.TIMELINE_HIDE_CLICK,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.NAVIGATION,
    });
  },

  timelineViewed(props) {
    Tracker.send({
      event: AnalyticsTrackingTypes.timelineViewed,
      interactionType: InteractionType.ACTIVE,
      chapterId: props.chapterId,
      chapterTitle: props.chapterTitle,
      chapterType: props.chapterType,
    });
  },

  timelineSlideClick(props, state, { slide }) {
    const slideId = slide.id;
    const slideType = Object.prototype.hasOwnProperty.call(slide, "video")
      ? "video"
      : "image";

    const { id: chapterId, title: chapterTitle } = props;

    Tracker.sendUIInteraction({
      item: UIInteractedItemType.TIMELINE_SLIDE_CLICK,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.NAVIGATION,
      chapterId,
      chapterTitle,
      slideId,
      slideType,
    });
  },

  verseLogoButtonClick() {
    Tracker.sendUIInteraction({
      item: UIInteractedItemType.VERSE_LOGO,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.NAVIGATION,
    });
  },

  fullscreenChange({ fullscreen }) {
    Tracker.sendUIInteraction({
      item: UIInteractedItemType.FULLSCREEN,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.PRESENTATION,
      fullscreen,
    });
  },

  qaScroll: once(() => {
    Tracker.sendPassiveUIInteraction({
      item: UIInteractedItemType.QA_SCROLL,
      type: UIInteractedTypeType.SCROLL,
      uiCategory: UIInteractedCategoryType.PRESENTATION,
    });
  }),

  qaSubmenu: once(() => {
    Tracker.sendUIInteraction({
      item: UIInteractedItemType.QA_SUBMENU,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.NAVIGATION,
    });
  }),

  backArrowClick: once(() => {
    Tracker.sendUIInteraction({
      item: UIInteractedItemType.BACK_ARROW_CLICK,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.NAVIGATION,
    });
  }),

  storyInfoChapterClick({ chapterId, chapterTitle }) {
    Tracker.sendUIInteraction({
      item: UIInteractedItemType.STORY_INFO_CHAPTER_CLICK,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.NAVIGATION,
      chapterId,
      chapterTitle,
    });
  },

  storyPreviewClick({ id: storyId, title: storyTitle }) {
    Tracker.sendUIInteraction({
      item: UIInteractedItemType.STORY_PREVIEW_CLICK,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.NAVIGATION,
      storyId,
      storyTitle,
    });
  },

  playOrPause(props) {
    const reduxState = reduxStore.getState();
    const playbackState = getPlaybackState(reduxState, props);
    const item = playbackState.playing
      ? UIInteractedItemType.PAUSE
      : UIInteractedItemType.PLAY;

    Tracker.sendUIInteraction({
      item,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.MEDIA_PLAYBACK,
    });
  },

  scrub(props, state) {
    const playbackState = props.playbackState || state.playbackState;

    Tracker.sendUIInteraction({
      item: UIInteractedItemType.SCRUB,
      positionAfterScrub: playbackState.time,
      positionBeforeScrub: playbackState.timeBeforeScrub,
      type: UIInteractedTypeType.CLICK,
      uiCategory: UIInteractedCategoryType.MEDIA_PLAYBACK,
    });
  },

  answerViewed(props) {
    const event = AnalyticsTrackingTypes.answerViewed;
    const question = props.currentQuestion;

    Tracker.send({
      event,
      chapterId: props.chapterId,
      chapterTitle: props.chapterTitle,
      questionId: question.id,
      questionTitle: question.title,
      interactionType: InteractionType.ACTIVE,
      levelDepth: InterviewUtils.depth(
        {
          questions: props.questions,
        },
        question,
      ),
    });
  },

  mediaProgress(props, state, manualTimeMs) {
    // Find `playbackState`. Error if it can't be found.
    const playbackState = props?.playbackState || state?.playbackState;
    if (!playbackState) {
      throw new Error(
        "Analytics cannot track mediaProgress without `playbackState`",
      );
    }
    if (!props.chapterId) {
      return;
    }

    const reduxState = reduxStore.getState();
    const chapterId = props.chapterId;
    const chapterEntity = getChapterEntity(reduxState, { chapterId });

    const chapterType = chapterEntity.type;

    // Save the current time. It can be passed an optional parameter. If
    // it wasn't passed, then pull it from `playbackState`.
    const timeMs = isUndefined(manualTimeMs)
      ? playbackState.time
      : manualTimeMs;

    // Find the duration of the current playback. Interviews do not use
    // the chapter duration because each question has a different
    // length.
    let durationMs;

    if (chapterType === ChapterType.INTERVIEW) {
      durationMs = playbackState.currentQuestion
        ? playbackState.currentQuestion.duration
        : playbackState.duration;
    } else if (chapterType === ChapterType.PATHFINDER) {
      // Shorten by 10 ms so that the loop doesn't send continually
      durationMs = chapterEntity.introductionVideo
        ? chapterEntity.introductionVideo.duration - 10
        : null;
    } else {
      durationMs = chapterEntity.duration;
    }

    if (!durationMs) {
      return;
    }

    // Calculate how often we're going to report progress. `GRANULARITY_PERCENTAGE`
    // specifies the base of how often to send playback progress.
    // `GRANULARITY_TIME_MIN_MS` and `GRANULARITY_TIME_MAX_MS` specifies the range the time report should be in
    // `reportInterval` will be the value for the media within the range based on the starting percent.
    // e.g., 5% but no less than every 15s and no more often than 2s depending on total media duration
    const GRANULARITY_PERCENTAGE = 5;
    const GRANULARITY_TIME_MAX_MS = 2000;
    const GRANULARITY_TIME_MIN_MS = 15000;
    /**
     * How often media playback events should be reported
     */
    const reportInterval = Math.round(
      Math.max(
        GRANULARITY_TIME_MAX_MS,
        Math.min(
          GRANULARITY_TIME_MIN_MS,
          durationMs * (GRANULARITY_PERCENTAGE / 100),
        ),
      ),
    );

    // Calculate the percent played and report true round number
    const percentagePlayed = Math.round((100 * timeMs) / durationMs);

    // Make sure `videoPlaybackProgressPercentage[props.chapterData]` is
    // defined.
    if (!videoPlaybackProgressPercentage.has(chapterId)) {
      videoPlaybackProgressPercentage.set(chapterId, 0);
    }

    // Make sure `videoPlaybackProgressTime[props.chapterData]` is defined. It is an array of
    // times that have been thus far reported.
    if (!videoPlaybackProgressTime.has(chapterId)) {
      videoPlaybackProgressTime.set(chapterId, []);
    }

    function reportMediaProgress(input) {
      const { percentagePlayed, timeMs } = input;

      const mediaId = getMediaId({
        chapterId,
        playbackState,
      });

      const videoId = getCurrentVideoId({
        chapterId,
        playbackState,
      });

      let mediaData = {
        chapterId,
        chapterTitle: chapterEntity.title,
        chapterType: titleize(chapterType),
        durationMs,
        mediaId,
        mediaType: getMediaType(chapterType),
        percentagePlayed,
        secondsPlayed: timeMs / 1000,
      };

      if (videoId) {
        mediaData = {
          ...mediaData,
          videoId,
        };
      }

      Tracker.sendMediaProgress(mediaData);
    }

    function reportFineGrainedMediaProgress(input) {
      const mediaId = getMediaId({
        chapterId,
        playbackState,
      });

      const videoId = getCurrentVideoId({
        chapterId,
        playbackState,
      });

      let mediaData = {
        chapterId,
        chapterTitle: chapterEntity.title,
        chapterType: titleize(chapterType),
        durationMs,
        mediaId,
        mediaType: getMediaType(chapterType),
        percentagePlayed: input.percentagePlayed,
        secondsPlayed: input.timeMs / 1000,
      };

      if (videoId) {
        mediaData = {
          ...mediaData,
          videoId,
        };
      }

      notifyExternalMediaPlayback(mediaData);
    }

    reportQuartiles({ chapterId, playbackState, reduxState, timeMs });

    if (
      chapterType === ChapterType.INTERVIEW &&
      !playbackState.currentQuestion
    ) {
      // wait until there is a question to report (could have been reset)
      return;
    }

    if (manualTimeMs === 0 || timeMs === durationMs) {
      // This is the beginning or the end of the video
      reportMediaProgress({
        timeMs,
        percentagePlayed,
      });
      reportFineGrainedMediaProgress({
        timeMs,
        percentagePlayed,
      });

      videoPlaybackProgressPercentage.set(chapterId, 0);
      videoPlaybackProgressTime.get(chapterId).push(0);
    } else {
      /** Rounded value of the current playback time */
      const seconds = Math.round(timeMs / 1000);

      /** Indicates if this time is an interval set to report completion metrics */
      const isReportInterval =
        timeMs >= reportInterval &&
        Math.round(timeMs / 1000) % Math.round(reportInterval / 1000) === 0;

      // Calculate if this is a new time to report
      const timeAlreadyReported = includes(
        videoPlaybackProgressTime.get(chapterId),
        seconds,
      );

      if (isReportInterval) {
        // This will fire multiple times on every interval; we need to make sure it's throttled on the other end
        reportFineGrainedMediaProgress({
          percentagePlayed,
          timeMs,
        });

        if (!timeAlreadyReported) {
          // confirm that new time is greater than already reported percentage
          if (
            percentagePlayed > videoPlaybackProgressPercentage.get(chapterId) &&
            percentagePlayed <= 100
          ) {
            videoPlaybackProgressPercentage.set(chapterId, percentagePlayed);
            videoPlaybackProgressTime.get(chapterId).push(seconds);

            reportMediaProgress({
              percentagePlayed,
              timeMs,
            });
          }
        }
      }
    }
  },

  answerViewedComplete(props, state) {
    const event = AnalyticsTrackingTypes.answerViewedComplete;
    const question = state.playbackState.currentQuestion;
    // Report video progress as hitting the end of the question.
    AnalyticsActions.mediaProgress(props, state, question.duration);

    Tracker.send({
      event,
      chapterId: props.chapterId,
      chapterTitle: props.chapterTitle,
      questionId: question.id,
      questionTitle: question.title,
      interactionType: InteractionType.PASSIVE,
      levelDepth: InterviewUtils.depth(
        {
          questions: props.questions,
        },
        question,
      ),
    });
  },
};

export const AnalyticsMixin = {
  UNSAFE_componentWillUpdate() {
    // Remove the event handlers on ref nodes.
    flushRefEvents.call(this, { eventListeners });
  },

  componentDidUpdate(oldProps, oldState) {
    if (this.constructor.analyticsActions) {
      this.constructor.analyticsActions
        // Only apply to actions that define `componentDidUpdateFilter`.
        .filter((action) => action.componentDidUpdateFilter)
        .forEach((actionDefinition) => {
          // Call `actionDefinition.action` if the filter returns `true`.
          if (
            actionDefinition.componentDidUpdateFilter.call(
              this,
              oldProps,
              oldState,
            )
          ) {
            const func = isFunction(actionDefinition.action)
              ? actionDefinition.action
              : AnalyticsActions[actionDefinition.action];

            func.call(null, this.props, this.state);
          }
        });
    }
  },

  componentDidMount() {
    addRefEvents.call(this, { eventListeners });
    addHandlerWrappers.call(this);
  },

  componentWillUnmount() {
    flushRefEvents.call(this, { eventListeners });
    eventListeners.delete(this);
  },
};
